import { composerSettings, helpers } from '@ddc-webplatform/cmsc-modules';
import { ContentType, TreeViewNode } from 'models';

/** Current site/profile ids, read once per tree pass and threaded through — see getContentType. */
export interface ContentTypeContext {
	siteId?: string;
	profileId?: string;
}

/** Reads the current site/profile ids once, to be reused across a whole tree pass by getContentType. */
export const getContentTypeContext = (): ContentTypeContext => composerSettings.getComposerSettings();

/**
 * Maps a leaf node's pageId to the content-type filter type shown in the UI,
 * following the ownership rule the team agreed on (Roger's precedence):
 *
 *   is it {my site id}_SITEBUILDER_?    → custom
 *   else is it {my profile id}_SITEBUILDER_? → program
 *   otherwise                            → site-default
 *
 *   - custom:       the pageId is owned by the CURRENT site — an exact prefix
 *                   match on {siteId}_SITEBUILDER_ (migration suffixes 2 / v9
 *                   are handled by getPageOwnershipData). The dealer has forced
 *                   the page into their own site.
 *   - program:      the pageId is owned by the account's profile —
 *                   {profileId}_SITEBUILDER_. Distributed content managed by an
 *                   OEM / partner program or a group profile (e.g. EDS accounts).
 *   - site-default: everything else — the base / global page whose id matches
 *                   neither the current site nor the account's profile.
 *
 * Site ownership wins over profile ownership. Directory/no-source nodes
 * (folders, external links) currently map to 'custom' — tentative, pending
 * a final decision on a dedicated bucket for them.
 *
 * `context` is the current site/profile ids — callers walking a whole tree
 * should fetch it once via getContentTypeContext and pass it in, rather than each
 * node re-reading composer settings (a full-tree pass would otherwise be N
 * settings lookups instead of one).
 *
 * NOTE: getPageOwnershipData / ComposerSettings expose a single profileId. If an
 * account can carry multiple profiles (OEM + group), program content from the
 * non-primary profiles would fall through to site-default. Revisit if/when a
 * list of account profile IDs becomes available on the mount event.
 */
export const getContentType = (
	node: TreeViewNode,
	context: ContentTypeContext = getContentTypeContext()
): ContentType | null => {
	if (!node.source) return 'custom';

	const { pageId } = node;
	if (!pageId) return 'site-default';

	const { siteId, profileId } = context;
	// Without a site id we can't verify ownership — fall through to default.
	if (!siteId) return 'site-default';

	const { isSiteOwnedPage, isProfileOwnedPage } = helpers.getPageOwnershipData(
		siteId,
		pageId,
		profileId
	);

	if (isSiteOwnedPage) return 'custom';
	if (isProfileOwnedPage) return 'program';
	return 'site-default';
};
